import React, {useEffect, useRef} from 'react';
import {Animated, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import CCText from '@/Atoms/CCText';
import {lbColors, lbRadius} from '../leaderboardTheme';
import {EngagementPresentation} from '../engagementPresentation';
import {MilestoneVM} from '../types';
import MilestoneBanner from './MilestoneBanner';

interface Props {
  streakCount: number;
  todayComplete: boolean;
  subline: string;
  presentation: EngagementPresentation;
  onSeeRewards: () => void;
  wide: boolean;
}

function usePulse() {
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 0.55,
          duration: 900,
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 1,
          duration: 900,
          useNativeDriver: false,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return pulse;
}

function isMilestoneAchieved(m: MilestoneVM, streakCount: number): boolean {
  return m.achievedThisPeriod || streakCount >= m.days;
}

/**
 * The slim "path to rewards" strip under the Engagement page heading: flame +
 * streak count, a 7-day-dot path leading into the configured milestone nodes,
 * a status banner, and a "See rewards" button that scrolls to the prize cards.
 * Design source: scratchpad/engagement_canvas.dc.html (#cc-prizes strip).
 *
 * Two render modes for the day-dot/node path, split on streakCount vs
 * firstMilestoneDays (the weekly draw threshold, 7):
 *  - Pre-day-7: today's day-dot-then-milestone-nodes strip, with the new
 *    week_checkpoint milestones (day 14/21) filtered out so it stays
 *    uncrowded before they're reachable.
 *  - Segment (streakCount >= 7): achieved milestones collapse into small
 *    checkmark chips on the left, the day-dot path only spans the CURRENT
 *    segment (prev milestone -> next milestone), and the next unachieved
 *    milestone renders as a full node with later ones dimmed.
 */
export default function PathToRewardsStrip({
  streakCount,
  todayComplete,
  subline,
  presentation,
  onSeeRewards,
  wide,
}: Props) {
  const {variant, bannerText, milestonesSorted, firstMilestoneDays} =
    presentation;
  const isRisk = variant === 'atRisk';
  const flamePulse = usePulse();
  // Guard against an empty milestones[] (misconfig/loading edge case) —
  // segment mode has nothing to anchor achieved/next off of, so fall back to
  // the pre-day-7 day-dot rendering, which already handles that gracefully.
  const isSegmentMode =
    streakCount >= firstMilestoneDays && milestonesSorted.length > 0;

  return (
    <LinearGradient
      colors={
        isRisk
          ? [lbColors.heroRiskGradientStart, lbColors.heroRiskGradientEnd]
          : [lbColors.heroGradientStart, lbColors.heroGradientEnd]
      }
      start={{x: 0, y: 0}}
      end={{x: 1, y: 1}}
      style={[styles.strip, !wide && styles.stripNarrow]}>
      {/* flame + streak count */}
      <View style={styles.flameBlock}>
        <Animated.View style={[styles.flameCircle, {opacity: flamePulse}]}>
          <CCText fontSize={22}>🔥</CCText>
        </Animated.View>
        <View>
          <View style={{flexDirection: 'row', alignItems: 'baseline', gap: 4}}>
            <CCText fontSize={26} fontWeight="bold" color={lbColors.goldInk}>
              {streakCount}
            </CCText>
            <CCText fontSize={12} fontWeight="bold" color="rgba(58,44,5,0.65)">
              day streak
            </CCText>
          </View>
          <CCText fontSize={10.5} fontWeight="bold" color="rgba(58,44,5,0.65)">
            {subline}
          </CCText>
        </View>
      </View>

      {/* day-dot path + milestone nodes. Wide: the connectors FLEX so the
          path distributes edge-to-edge across the panel (no dead whitespace);
          narrow keeps fixed connectors inside a horizontal scroll. */}
      <View style={[styles.pathCard, !wide && styles.pathCardNarrow]}>
        {(() => {
          const pathContent = isSegmentMode ? (
            <SegmentPathContent
              streakCount={streakCount}
              todayComplete={todayComplete}
              milestonesSorted={milestonesSorted}
              wide={wide}
            />
          ) : (
            <PreDay7PathContent
              streakCount={streakCount}
              todayComplete={todayComplete}
              milestonesSorted={milestonesSorted.filter(
                m => m.kind !== 'week_checkpoint',
              )}
              firstMilestoneDays={firstMilestoneDays}
              wide={wide}
            />
          );
          return wide ? (
            <View style={[styles.pathRow, styles.pathRowWide]}>
              {pathContent}
            </View>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.pathRow}>{pathContent}</View>
            </ScrollView>
          );
        })()}
      </View>

      {/* banner + CTA */}
      <View style={[styles.sideCol, !wide && styles.sideColNarrow]}>
        <MilestoneBanner variant={variant} text={bannerText} compact />
        <Pressable
          onPress={onSeeRewards}
          style={({pressed}) => [
            styles.seeRewards,
            pressed && {opacity: 0.85},
          ]}>
          <CCText fontSize={13}>🎁</CCText>
          <CCText fontSize={11} fontWeight="bold" color={lbColors.gold}>
            See rewards
          </CCText>
        </Pressable>
      </View>
    </LinearGradient>
  );
}

// Today's rendering: day dots 1..firstMilestoneDays, then every (non
// checkpoint) milestone node in order.
function PreDay7PathContent({
  streakCount,
  todayComplete,
  milestonesSorted,
  firstMilestoneDays,
  wide,
}: {
  streakCount: number;
  todayComplete: boolean;
  milestonesSorted: MilestoneVM[];
  firstMilestoneDays: number;
  wide?: boolean;
}) {
  const nextDay = streakCount + 1;
  const days = Array.from(
    {length: Math.max(firstMilestoneDays, 1)},
    (_, i) => i + 1,
  );

  return (
    <>
      {days.map(d => {
        const done = streakCount >= d;
        const isNext = !done && d === nextDay;
        return (
          <React.Fragment key={d}>
            <View style={[styles.dotCol, wide && styles.dotColWide]}>
              <PulsingDot pulse={isNext && !todayComplete}>
                <View
                  style={[
                    styles.dot,
                    wide && styles.dotWide,
                    {
                      backgroundColor: done ? lbColors.gold : '#FFFFFF',
                      borderColor: done
                        ? lbColors.goldDeep
                        : isNext
                        ? lbColors.orange
                        : lbColors.line,
                    },
                  ]}>
                  <CCText
                    fontSize={wide ? 11 : 10}
                    fontWeight="bold"
                    color={
                      done
                        ? lbColors.goldInk
                        : isNext
                        ? lbColors.orange
                        : lbColors.faint
                    }>
                    {done ? '✓' : d}
                  </CCText>
                </View>
              </PulsingDot>
              {d === 1 || d === days.length ? (
                <CCText fontSize={7.5} fontWeight="bold" color={lbColors.muted}>
                  {d === 1 ? 'Day 1' : `Day ${d}`}
                </CCText>
              ) : null}
            </View>
            <View style={[styles.connector, wide && styles.connectorGrow]}>
              <View
                style={[styles.connectorFill, {width: done ? '100%' : '0%'}]}
              />
            </View>
          </React.Fragment>
        );
      })}
      {milestonesSorted.map((m, i) => (
        <React.Fragment key={m.id}>
          {i > 0 ? (
            <MilestoneConnector
              fromDays={milestonesSorted[i - 1].days}
              toDays={m.days}
              streakCount={streakCount}
              grow={wide}
            />
          ) : null}
          <MilestoneNode
            milestone={m}
            streakCount={streakCount}
            todayComplete={todayComplete}
            wide={wide}
          />
        </React.Fragment>
      ))}
    </>
  );
}

// streakCount >= 7: achieved milestones collapse to checkmark chips on the
// left, day dots only span the CURRENT segment (last achieved -> next
// milestone), the next unachieved milestone gets a full node, and any later
// ones render dimmed/smaller — except the monthly (Gukesh) node, which keeps
// its full hero size just dimmed until it becomes the target. At 30+ every
// milestone is a chip and the monthly node becomes the celebratory node.
function SegmentPathContent({
  streakCount,
  todayComplete,
  milestonesSorted,
  wide,
}: {
  streakCount: number;
  todayComplete: boolean;
  milestonesSorted: MilestoneVM[];
  wide?: boolean;
}) {
  const achieved = milestonesSorted.filter(m =>
    isMilestoneAchieved(m, streakCount),
  );
  const unachieved = milestonesSorted.filter(
    m => !isMilestoneAchieved(m, streakCount),
  );
  const allAchieved = unachieved.length === 0;
  // Once everything is achieved the highest (monthly/Gukesh) milestone
  // becomes the celebratory hero node instead of just another chip.
  const chipMilestones = allAchieved ? achieved.slice(0, -1) : achieved;
  const celebratory = allAchieved ? achieved[achieved.length - 1] : undefined;
  const prev = achieved[achieved.length - 1];
  const next = unachieved[0];
  const rightMilestones = next ? [next, ...unachieved.slice(1)] : [];

  const segStart = (prev?.days ?? 0) + 1;
  const segEnd = next?.days ?? segStart - 1;
  const segDays =
    !allAchieved && segEnd >= segStart
      ? Array.from({length: segEnd - segStart + 1}, (_, i) => segStart + i)
      : [];
  const nextDay = streakCount + 1;

  return (
    <>
      {chipMilestones.map(m => (
        <AchievedChip key={m.id} milestone={m} />
      ))}
      {!allAchieved && chipMilestones.length > 0 ? (
        <View style={[styles.connector, wide && styles.connectorGrow]}>
          <View style={[styles.connectorFill, {width: '100%'}]} />
        </View>
      ) : null}
      {segDays.map(d => {
        const done = streakCount >= d;
        const isNext = !done && d === nextDay;
        return (
          <React.Fragment key={d}>
            <View style={[styles.dotCol, wide && styles.dotColWide]}>
              <PulsingDot pulse={isNext && !todayComplete}>
                <View
                  style={[
                    styles.dot,
                    wide && styles.dotWide,
                    {
                      backgroundColor: done ? lbColors.gold : '#FFFFFF',
                      borderColor: done
                        ? lbColors.goldDeep
                        : isNext
                        ? lbColors.orange
                        : lbColors.line,
                    },
                  ]}>
                  <CCText
                    fontSize={wide ? 11 : 10}
                    fontWeight="bold"
                    color={
                      done
                        ? lbColors.goldInk
                        : isNext
                        ? lbColors.orange
                        : lbColors.faint
                    }>
                    {done ? '✓' : d}
                  </CCText>
                </View>
              </PulsingDot>
              {d === segDays[0] || d === segDays[segDays.length - 1] ? (
                <CCText fontSize={7.5} fontWeight="bold" color={lbColors.muted}>
                  {`Day ${d}`}
                </CCText>
              ) : null}
            </View>
            <View style={[styles.connector, wide && styles.connectorGrow]}>
              <View
                style={[styles.connectorFill, {width: done ? '100%' : '0%'}]}
              />
            </View>
          </React.Fragment>
        );
      })}
      {rightMilestones.map((m, i) => (
        <React.Fragment key={m.id}>
          {i > 0 ? (
            <MilestoneConnector
              fromDays={rightMilestones[i - 1].days}
              toDays={m.days}
              streakCount={streakCount}
              grow={wide}
            />
          ) : null}
          <View
            style={
              i > 0
                ? [
                    styles.dimmedNode,
                    m.period !== 'monthly' && styles.dimmedNodeScaled,
                  ]
                : undefined
            }>
            <MilestoneNode
              milestone={m}
              streakCount={streakCount}
              todayComplete={todayComplete}
              wide={wide}
            />
          </View>
        </React.Fragment>
      ))}
      {allAchieved && celebratory ? (
        <MilestoneNode
          milestone={celebratory}
          streakCount={streakCount}
          todayComplete={todayComplete}
          wide={wide}
        />
      ) : null}
    </>
  );
}

// Compact "done, shifted left" pill for an already-achieved milestone.
function AchievedChip({milestone}: {milestone: MilestoneVM}) {
  const label = milestone.label || milestone.prize.short;
  return (
    <LinearGradient
      colors={[lbColors.greenBg, '#FFFFFF']}
      start={{x: 0, y: 0}}
      end={{x: 1, y: 0}}
      style={styles.achievedChip}>
      <CCText fontSize={9.5} fontWeight="bold" color={lbColors.greenInk}>
        {`✓ ${label}`}
      </CCText>
    </LinearGradient>
  );
}

function PulsingDot({
  pulse,
  children,
}: {
  pulse: boolean;
  children: React.ReactNode;
}) {
  const anim = usePulse();
  if (!pulse) {
    return <>{children}</>;
  }
  return <Animated.View style={{opacity: anim}}>{children}</Animated.View>;
}

function MilestoneConnector({
  fromDays,
  toDays,
  streakCount,
  grow,
}: {
  fromDays: number;
  toDays: number;
  streakCount: number;
  grow?: boolean;
}) {
  const span = Math.max(1, toDays - fromDays);
  const pct = Math.min(
    100,
    Math.max(0, Math.round(((streakCount - fromDays) / span) * 100)),
  );
  return (
    <View style={[styles.connector, grow && styles.connectorGrow]}>
      <View style={[styles.connectorFill, {width: `${pct}%`}]} />
    </View>
  );
}

function MilestoneNode({
  milestone,
  streakCount,
  todayComplete,
  wide,
}: {
  milestone: MilestoneVM;
  streakCount: number;
  todayComplete: boolean;
  wide?: boolean;
}) {
  const achieved =
    milestone.achievedThisPeriod || streakCount >= milestone.days;
  const isNext = !achieved && milestone.daysLeft === 1;
  const icon =
    milestone.kind === 'week_checkpoint'
      ? '🚩'
      : milestone.period === 'weekly'
      ? '🎁'
      : milestone.period === 'monthly'
      ? '📱'
      : '🏆';
  const bg = achieved ? lbColors.greenBg : lbColors.bannerGradientStart;
  const border = achieved
    ? lbColors.green
    : isNext
    ? lbColors.orange
    : lbColors.line;
  const stateBg = achieved
    ? lbColors.green
    : isNext
    ? lbColors.orange
    : lbColors.bgAlt;
  const stateColor = achieved || isNext ? '#FFFFFF' : lbColors.inkSoft;
  const stateText = achieved
    ? "You're in! ✓"
    : milestone.daysLeft === 1
    ? '1 day to go'
    : `${milestone.daysLeft} days to go`;

  return (
    <View style={styles.nodeCol}>
      <PulsingDot pulse={isNext && !todayComplete}>
        <View
          style={[
            styles.node,
            wide && styles.nodeWide,
            {backgroundColor: bg, borderColor: border},
          ]}>
          <CCText fontSize={wide ? 20 : 18}>{icon}</CCText>
        </View>
      </PulsingDot>
      <CCText
        fontSize={7.5}
        fontWeight="bold"
        color={lbColors.goldInk}
        style={{textAlign: 'center'}}>
        {milestone.label || milestone.prize.short}
      </CCText>
      <View style={[styles.stateChip, {backgroundColor: stateBg}]}>
        <CCText fontSize={7} fontWeight="bold" color={stateColor}>
          {stateText}
        </CCText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    borderRadius: lbRadius.lg,
    padding: 12,
    marginBottom: 14,
  },
  stripNarrow: {flexDirection: 'column', alignItems: 'stretch', gap: 10},
  flameBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 158,
  },
  flameCircle: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: 'rgba(255,255,255,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pathCard: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.93)',
    borderRadius: lbRadius.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  pathCardNarrow: {flex: undefined},
  pathRow: {flexDirection: 'row', alignItems: 'flex-start'},
  // Wide: fill the white panel edge-to-edge; the connectors carry the slack.
  pathRowWide: {width: '100%', paddingHorizontal: 4},
  dotCol: {alignItems: 'center', gap: 4, minWidth: 26},
  dotColWide: {minWidth: 30},
  dot: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotWide: {width: 28, height: 28, borderRadius: 14},
  connector: {
    width: 12,
    height: 4,
    borderRadius: 3,
    backgroundColor: lbColors.line,
    marginTop: 10,
    overflow: 'hidden',
  },
  // Wide: connectors flex-grow so the path distributes across the panel.
  connectorGrow: {width: undefined, flex: 1, minWidth: 10, marginTop: 12},
  connectorFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: lbColors.orange,
  },
  nodeCol: {alignItems: 'center', gap: 4, minWidth: 56, marginTop: -8},
  node: {
    width: 40,
    height: 40,
    borderRadius: 13,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nodeWide: {width: 46, height: 46, borderRadius: 15},
  stateChip: {paddingHorizontal: 6, paddingVertical: 2, borderRadius: 9},
  sideCol: {gap: 7, minWidth: 220, maxWidth: 245},
  sideColNarrow: {minWidth: undefined, maxWidth: undefined},
  seeRewards: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: lbColors.dark,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: lbRadius.sm + 1,
  },
  // Segment mode (streakCount >= 7) additions.
  achievedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: lbRadius.pill,
    borderWidth: 1,
    borderColor: lbColors.green,
    marginRight: 4,
  },
  // Later (not-yet-target) milestone nodes: dimmed, and shrunk unless it's
  // the monthly/Gukesh node, which keeps its full hero size.
  dimmedNode: {opacity: 0.55},
  dimmedNodeScaled: {transform: [{scale: 0.82}]},
});
